import { CommonModule, DatePipe, formatDate, Location } from '@angular/common';
import { ChangeDetectorRef, Component, Input, OnDestroy } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { GoogleMapComponent } from "../../google-map/google-map.component";
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule, MAT_DATE_FORMATS, MAT_DATE_LOCALE, DateAdapter, NativeDateAdapter } from '@angular/material/core';
import { ActivatedRoute, Router } from '@angular/router';
import { APIService } from '../../fleet-management/backend-service/backend-service';
import { SharedService } from '../../share-data/shared.service';
import { HttpResponse } from '@angular/common/http';
import { GlobalVariablesService } from '../../global.service';
import { SettingData } from '../../models/settings.model';
import { Injectable } from '@angular/core';
import { NotificationService } from '../../shared/services/notification.service';
import { LoadingService } from '../../services/loading.service';
import { Subscription } from 'rxjs';
import { PhoneFormatDirective } from '../../shared/directives/phone-format.directive';
import { PhoneFormatPipe } from '../../shared/pipes/phone-format.pipe';

export const DATE_FORMATS = {
  parse: {
    dateInput: 'dd/MM/yyyy',
  },
  display: {
    dateInput: 'dd/MM/yyyy',
    monthYearLabel: 'MMM yyyy',
    dateA11yLabel: 'dd/MM/yyyy',
    monthYearA11yLabel: 'MMMM yyyy'
  }
};

@Injectable()
export class CustomDateAdapter extends NativeDateAdapter {
  override format(date: Date, displayFormat: Object): string {
    if (displayFormat === 'dd/MM/yyyy') {
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    }
    return super.format(date, displayFormat);
  }

  override parse(value: any): Date | null {
    if (typeof value === 'string' && value.includes('/')) {
      const parts = value.split('/');
      if (parts.length === 3) {
        const day = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10) - 1;
        const year = parseInt(parts[2], 10);
        return new Date(year, month, day);
      }
    }
    return super.parse(value);
  }
}
@Component({
  selector: 'app-customer-registration',
  imports: [
    FormsModule,
    MatAutocompleteModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatRadioModule,
    MatSelectModule,
    ReactiveFormsModule,
    CommonModule,
    GoogleMapComponent,
    MatDatepickerModule,
    MatNativeDateModule,
    MatProgressSpinnerModule,
    PhoneFormatDirective,
    PhoneFormatPipe
  ],
  providers: [
    DatePipe,
    { provide: MAT_DATE_LOCALE, useValue: 'en-GB' },
    { provide: DateAdapter, useClass: CustomDateAdapter },
    { provide: MAT_DATE_FORMATS, useValue: DATE_FORMATS }
  ],
  templateUrl: './customer-registration.component.html',
  styleUrl: './customer-registration.component.scss'
})
export class CustomerRegistrationComponent implements OnDestroy {
  nameValue: string = '';
  formattedDate: string = '';
  address: string = '';
  remark: string = '';
  vatNumber: string = '';
  expertise: string = '';
  businessOptions: SettingData[] = [];
  businessSelectedValue: SettingData = { id: '', name: '' };
  creditTermInvoiceOptions: SettingData[] = [];
  creditTermInvoiceSelectedValue: SettingData = { id: '', name: '' };
  financialRatingOptions: SettingData[] = [];
  financialRatingSelectedValue: SettingData = { id: '', name: '' };
  currentLocation: google.maps.LatLngLiteral | null = null;
  isEdit: string = '';
  userData: any;
  isLoading: boolean = false;
  private loadingSubscription: Subscription;

  branchAddress: string = '';
  branchContacts = [{ name: '', phone: '', position: '' }];
  branchLocation: google.maps.LatLngLiteral | null = null;
  selectedBranchIndex: number = -1;

  // Add branches array to store multiple branches
  branches: any[] = [];
  allOriginBranches: any[] = [];
  contacts = [
    { name: '', phone: '', position: '' }
  ];

  constructor(private router: Router, private route: ActivatedRoute, private apiService: APIService,
    private sharedService: SharedService, public location: Location, private globalVariable: GlobalVariablesService,
    private datePipe: DatePipe, private cdr: ChangeDetectorRef,
    private notification: NotificationService, public loadingService: LoadingService) {
    // Subscribe to centralized loading state
    this.loadingSubscription = this.loadingService.isLoading$.subscribe(
      loading => this.isLoading = loading
    );
  }

  ngOnDestroy() {
    if (this.loadingSubscription) {
      this.loadingSubscription.unsubscribe();
    }
  }

  ngOnInit() {
    this.contacts = [];
    this.formattedDate = formatDate(new Date(), 'yyyy-MM-dd', 'en-US');
    this.setBusinessType();
    this.setInvoiceDate();
    this.setFinanceRating();

    this.route.queryParams.subscribe(async params => {
      this.isEdit = params["isEdit"];
      if (this.isEdit == 'true') {
        try {
          this.userData = this.sharedService.getData();
          this.loadEditData(this.userData);
          console.log("Share data:", this.userData);

          // Map basic fields
          this.nameValue = this.userData["name"] || '';
          this.vatNumber = this.userData["vatNumber"] || '';
          this.remark = this.userData["remark"] || '';

          // Map business type dropdown
          if (this.userData["businessType"]) {
            this.businessSelectedValue = this.globalVariable.SETTING_BUSINESS_TYPES.find(
              (obj: any) => obj.id === this.userData["businessType"]
            ) || { id: '', name: '' };
          }

          // Map date field - handle the ISO date string
          if (this.userData["yearsInBusiness"]) {
            const date = new Date(this.userData["yearsInBusiness"]);
            this.expertise = this.datePipe.transform(date, 'yyyy-MM-dd') || '';
          }

          // Map invoice date dropdown
          if (this.userData["invoiceDate"]) {
            this.creditTermInvoiceSelectedValue = this.globalVariable.SETTING_CREDIT_TERMS.find(
              (obj: any) => obj.id === this.userData["invoiceDate"]
            ) || { id: '', name: '' };
          }

          // Map financial rating dropdown
          if (this.userData["financialRating"]) {
            this.financialRatingSelectedValue = this.globalVariable.SETTING_FINANCIAL_RATINGS.find(
              (obj: any) => obj.id === this.userData["financialRating"]
            ) || { id: '', name: '' };
          }

          // Map contacts array
          if (this.userData["locations"] && this.userData["locations"].length > 0) {
            for (let index = 0; index < this.userData["locations"].length; index++) {
              this.allOriginBranches.push({
                id: this.userData.locations[index].id || '',
                address: this.userData.locations[index].address || '',
                locationPin: this.userData.locations[index].locationPin || '',
                notes: this.userData.locations[index].notes || '',
                isDefault: this.userData.locations[index].isDefault || false,
                isActive: this.userData.locations[index].isActive || false,
                contacts: this.userData.locations[index].contacts.map((contact: any) => ({
                  contactName: contact.contactName || '',
                  phoneNumber: contact.phoneNumber || '',
                  position: contact.position || ''
                })),
              });

              if (index == this.userData["locations"].length - 1) {
                this.selectedBranchIndex = index; // Set the selected branch index to the last one
                this.address = this.userData.locations[index].address || '';
                this.contacts = this.userData.locations[index].contacts.map((contact: any) => ({
                  name: contact.contactName || '',
                  phone: contact.phoneNumber || '',
                  position: contact.position || ''
                }));
                if (this.userData.locations[index].locationPin) {
                  const locationParts = this.userData.locations[index].locationPin.split(",");
                  if (locationParts.length === 2) {
                    this.currentLocation = {
                      lat: parseFloat(locationParts[0]),
                      lng: parseFloat(locationParts[1])
                    };
                  }
                  console.log("Current Location: ", this.currentLocation);
                }
              }
              else {
                this.branches.push({
                  id: this.userData.locations[index].id || '',
                  address: this.userData.locations[index].address || '',
                  locationPin: this.userData.locations[index].locationPin || '',
                  notes: this.userData.locations[index].notes || '',
                  isDefault: this.userData.locations[index].isDefault || false,
                  isActive: this.userData.locations[index].isActive || false,
                  contacts: this.userData.locations[index].contacts.map((contact: any) => ({
                    contactName: contact.contactName || '',
                    phoneNumber: contact.phoneNumber || '',
                    position: contact.position || ''
                  })),
                });
              }
            }
          } else {
            // Ensure at least one empty contact if no contacts exist
            this.branches = [{
              id: '',
              address: '',
              locationPin: '',
              notes: '',
              isDefault: true,
              isActive: true,
              contacts: [{ name: '', phone: '', position: '' }]
            }];
          }

          this.cdr.detectChanges(); // Ensure changes are reflected
        } catch (error) {
          console.error('Error mapping user data:', error);
          // Initialize with default values if mapping fails
          this.initializeDefaultValues();
        }
      } else {
        // Initialize default values for new registration
        this.initializeDefaultValues();
      }

      // Ensure business options are populated
      for (const role of this.globalVariable.SETTING_BUSINESS_TYPES || []) {
        if (!this.businessOptions.some(r => r.id === role.id)) {
          this.businessOptions.push(role);
        }
      }
    });
  }

  // Helper method to initialize default values
  private initializeDefaultValues() {
    this.nameValue = '';
    this.address = '';
    this.vatNumber = '';
    this.remark = '';
    this.expertise = '';
    this.currentLocation = null;
    this.businessSelectedValue = { id: '', name: '' };
    this.creditTermInvoiceSelectedValue = { id: '', name: '' };
    this.financialRatingSelectedValue = { id: '', name: '' };
    this.contacts = [{ name: '', phone: '', position: '' }];
  }

  // Alternative approach: Create a dedicated method for mapping data
  mapUserDataToForm(data: any) {
    this.nameValue = data.name || '';
    this.address = data.address || '';
    this.vatNumber = data.vatNumber || '';
    this.remark = data.remark || '';

    // Handle location
    if (data.locationPin) {
      const [lat, lng] = data.locationPin.split(",").map(parseFloat);
      this.currentLocation = { lat, lng };
    }

    // Handle dropdowns
    this.businessSelectedValue = this.globalVariable.SETTING_BUSINESS_TYPES.find(
      (obj: any) => obj.id === data.businessType
    ) || { id: '', name: '' };

    this.creditTermInvoiceSelectedValue = this.globalVariable.SETTING_CREDIT_TERMS.find(
      (obj: any) => obj.id === data.invoiceDate
    ) || { id: '', name: '' };

    this.financialRatingSelectedValue = this.globalVariable.SETTING_FINANCIAL_RATINGS.find(
      (obj: any) => obj.id === data.financialRating
    ) || { id: '', name: '' };

    // Handle date
    if (data.yearsInBusiness) {
      const date = new Date(data.yearsInBusiness);
      this.expertise = this.datePipe.transform(date, 'yyyy-MM-dd') || '';
    }

    // Handle contacts
    if (data.contacts && data.contacts.length > 0) {
      this.contacts = data.contacts.map((contact: any) => ({
        name: contact.contactName || '',
        phone: contact.phoneNumber || '',
        position: contact.position || ''
      }));
    }
  }

  // You can also add a method to handle the data passed directly (not from shared service)
  loadEditData(data: any) {
    this.userData = data;
    this.mapUserDataToForm(data);
    this.cdr.detectChanges();
  }

  setBusinessType() {
    console.log("Business Types: ", this.globalVariable.SETTING_BUSINESS_TYPES)
    for (const role of this.globalVariable.SETTING_BUSINESS_TYPES || []) {
      if (!this.businessOptions.some(r => r["id"] === role.id)) {
        this.businessOptions.push(role);
      }
    }
  }

  setInvoiceDate() {
    for (const role of this.globalVariable.SETTING_CREDIT_TERMS || []) {
      if (!this.creditTermInvoiceOptions.some(r => r["id"] === role.id)) {
        this.creditTermInvoiceOptions.push(role);
      }
    }
  }
  setFinanceRating() {
    for (const role of this.globalVariable.SETTING_FINANCIAL_RATINGS || []) {
      if (!this.financialRatingOptions.some(r => r["id"] === role.id)) {
        this.financialRatingOptions.push(role);
      }
    }
  }

  handleMapClick(marker: any) {
    console.log('Customer Marker:', marker);
    console.log("Customer => Lat: ", marker.latLng.lat(), " Long: ", marker.latLng.lng())
    this.currentLocation = {
      lat: marker.latLng.lat(),
      lng: marker.latLng.lng()
    };
  }

  handleLocationChanged(newLocation: google.maps.LatLngLiteral) {
    // Handle the new location received from the child component
    this.currentLocation = newLocation;
    console.log('Location received in parent:', this.currentLocation);
  }
  onYearSelected(event: any) {
    console.log('Selected Year:', event.value);
    this.formattedDate = '04-03-2025'
  }



  addContact() {
    this.contacts.push({ name: '', phone: '', position: '' });
  }

  removeContact(index: number) {
    if (this.contacts.length > 1) {
      this.contacts.splice(index, 1);
    }
  }
  addBranchBtnAction() {
    // Basic validation for branch data
    if (this.address.length < 1) {
      this.notification.warning('Please enter Address for the branch');
      return;
    }

    if (this.contacts[0].name.length < 1) {
      this.notification.warning('Please enter at least one contact for the branch');
      return;
    }

    // if (!this.currentLocation) {
    //   this.notification.warning('Please select a location on the map for the branch');
    //   return;
    // }
    this.currentLocation = this.currentLocation || { lat: 0, lng: 0 }; // Default to (0,0) if not set

    // Create branch data object
    const branchData = {
      address: this.address,
      locationPin: this.currentLocation.lat + "," + this.currentLocation.lng,
      isDefault: true,
      contacts: this.contacts.map(contact => ({
        contactName: contact.name,
        phoneNumber: contact.phone,
        position: contact.position
      }))
    };
    this.branches.push(branchData);

    // Show success alert
    // window.alert("New branch added successfully!");

    // Log the branch data (you can process this data as needed)
    console.log("Branch Array:", this.branches);

    // Optional: Clear the branch form fields after adding
    this.clearBranchFields();
  }

  // Helper method to clear branch-specific fields
  private clearBranchFields() {
    this.address = '';
    this.currentLocation = null;
    this.contacts = [{ name: '', phone: '', position: '' }];
  }
  onBranchRowClick(branchData: any, index: number) {
    console.log('Branch row clicked:', branchData, index);
    this.branches = [];
    for (let i = 0; i < this.allOriginBranches.length; i++) {
      if (branchData.id != this.allOriginBranches[i].id) {
        this.selectedBranchIndex = i; // Set the selected branch index
        this.address = branchData.address || '';
        if (this.userData.locations[index].locationPin) {
          const locationParts = this.userData.locations[index].locationPin.split(",");
          if (locationParts.length === 2) {
            this.currentLocation = {
              lat: parseFloat(locationParts[0]),
              lng: parseFloat(locationParts[1])
            };
          }
        }
        this.currentLocation = {
          lat: parseFloat(this.allOriginBranches[i].locationPin.split(",")[0]),
          lng: parseFloat(this.allOriginBranches[i].locationPin.split(",")[1])
        };
        this.contacts = branchData.contacts.map((contact: any) => ({
          name: contact.contactName || '',
          phone: contact.phoneNumber || '',
          position: contact.position || ''
        }));


        this.branches.push({
          id: this.allOriginBranches[i].id || '',
          address: this.allOriginBranches[i].address || '',
          locationPin: this.allOriginBranches[i].locationPin || '',
          notes: this.allOriginBranches[i].notes || '',
          isDefault: this.allOriginBranches[i].isDefault || false,
          isActive: this.allOriginBranches[i].isActive || false,
          contacts: this.allOriginBranches[i].contacts.map((contact: any) => ({
            contactName: contact.contactName || '',
            phoneNumber: contact.phoneNumber || '',
            position: contact.position || ''
          })),
        });
        console.log("Selected Branch: ", this.address, this.currentLocation, this.contacts);

      }
    }
  }

  submitButtonAction() {
    let loginData = JSON.parse(localStorage.getItem('loginData')!)
    if (this.address.length > 0 && this.contacts.length > 0) {
      this.currentLocation = this.currentLocation || { lat: 0, lng: 0 };
      const branchData = {
        address: this.address,
        locationPin: this.currentLocation.lat + "," + this.currentLocation.lng,
        isDefault: false,
        contacts: this.contacts.map(contact => ({
          contactName: contact.name,
          phoneNumber: contact.phone,
          position: contact.position
        }))
      };
      this.branches.push(branchData);
    }
    if (this.nameValue.length < 1) {
      this.notification.warning('Please enter Name')
      return;
    }
    if (this.branches.length < 1) {
      this.notification.warning('Please add the branch information')
      return;
    }
    if (this.businessSelectedValue.id.length < 1) {
      this.notification.warning('Please select Business Type')
      return;
    }
    if (this.vatNumber.length < 1) {
      this.notification.warning('Please select VAT Number')
      return;
    }
    if (this.expertise.length < 1) {
      this.notification.warning('Please enter Experience')
      return;
    }
    if (this.creditTermInvoiceSelectedValue.id.length < 1) {
      this.notification.warning('Please enter Credit Term Invoice')
      return;
    }
    if (this.financialRatingSelectedValue.id.length < 1) {
      this.notification.warning('Please select Financial Rating')
      return;
    }
    let jsonData = {
      "name": this.nameValue,
      "address": this.address,
      "locationPin": this.currentLocation?.lat + "," + this.currentLocation?.lng,
      "businessType": this.businessSelectedValue.id,
      "vatNumber": this.vatNumber,
      "yearsInBusiness": this.expertise,
      "invoiceDate": this.creditTermInvoiceSelectedValue.id,
      "financialRating": this.financialRatingSelectedValue.id,
      "remark": this.remark,
      "locations": this.branches,
      // "contacts": this.contacts.map(contact => ({
      //   "contactName": contact.name,
      //   "phoneNumber": contact.phone,
      //   "position": contact.position
      // })),
    }
    console.log("Post_Data: ", jsonData)
    let endpoint = '';
    // Loading is now handled automatically by the HTTP interceptor
    if (this.isEdit == 'true') {
      endpoint = '/api/customers/' + this.userData["id"];
      this.apiService.put(endpoint, jsonData, loginData.token)
        .subscribe({
          next: (response: HttpResponse<any>) => {
            console.log('HTTP Status:', response.status);
            console.log('/api/customers/edit' + ',\n' + 'Edit_Response:', response.body);

            if (response.status === 200) {
              this.notification.success('Update User successfully');
            }
            else {
              this.notification.error('Update User failed. Please try again.');
            }
            this.location.back();
          },
          error: err => {
            console.error('Error:', err);
          }
        });
    }
    else {
      console.log("User data: ", jsonData)
      endpoint = '/api/customers/register';
      this.apiService.post(endpoint, jsonData, loginData.token).subscribe({
        next: response => {
          console.log('HTTP Status:', response.status);
          console.log('Response Body:', response.body);
          if (response.status === 200 || response.status === 201) {
            this.notification.success('Register Customer successfully');
          }
          else {
            this.notification.error('Register customer failed. Please try again.');
          }
          this.router.navigate(['/home/customer-management']);
        },
        error: error => {
          console.error('Error:', error);
        }
      });
    }
    console.log("endpoint: ", endpoint)

  }

}
